import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { Token } from 'vvs-sdk'
import { Flex, Text, Skeleton, Box, ArrowForwardIcon, ArrowBackIcon, useMatchBreakpoints, Button } from 'vvs-uikit'
import styled from 'styled-components'
import useTheme from 'hooks/useTheme'
import useGelatoLimitOrdersHistory from 'state/limitOrders/useGelatoLimitOrdersHistory'
import useFormattedOrderData from 'state/limitOrders/useFormattedOrderData'
import { useTranslation } from 'contexts/Localization'
import { TokenImage } from 'components/TokenImage'
import useGelatoLimitOrdersHandlers from 'state/limitOrders/useGelatoLimitOrdersHandlers'

const ITEMS_PER_INFO_TABLE_PAGE = 3

const TableWrapper = styled(Flex)`
  width: 100%;
  padding-top: 16px;
  flex-direction: column;
  gap: 16px;
  background-color: ${({ background }) => background};
  border-radius: ${({ border }) => border};
`

const Break = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.cardBorder};
  width: 100%;
`

const PageButtons = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.2em;
  margin-bottom: 1.2em;
`

const Arrow = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  padding: 0 20px;
  :hover {
    cursor: pointer;
  }
`

const PastOrderDataRow = ({ order }) => {
  const {
    inputToken,
    outputToken,
    inputAmount,
    outputAmount,
    executionPrice,
    invertedExecutionPrice,
    isOpen,
    isCancelled,
    isExecuted,
    isSubmissionPending,
    isCancellationPending,
    isExpired,
    updatedAt,
    realExecutionPriceAsString,
  } = useFormattedOrderData(order)
  const { theme } = useTheme()
  const { isMobile } = useMatchBreakpoints()

  return (
    <Flex flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-around" mx="20px">
      <Flex alignItems="top" width="80%">
        <TokenImage token={outputToken as Token} width={35} height={35} />
        <Flex flexDirection="column" height="100%" width="100%" ml="10px">
          <Flex alignItems="center" mb="5px">
            <Box style={{ marginRight: '5px' }}>
              <Text color="text">
                {inputAmount} {inputToken?.symbol}
              </Text>
            </Box>
            <ArrowForwardIcon fill={theme.colors.primary} style={{ marginRight: '5px' }} />
            <Box>
              <Text color="text">
                {outputAmount} {outputToken?.symbol}
              </Text>
            </Box>
          </Flex>
        </Flex>
      </Flex>
      <Flex
        alignItems="center"
        justifyContent={isMobile ? 'space-between' : 'center'}
        flexDirection={!isMobile ? 'column' : 'row-reverse'}
      >
        <Box style={{ marginBottom: !isMobile ? '10px' : '0px', marginTop: isMobile ? '15px' : '0px' }}>
          <Text bold color="textSubtle">
            {updatedAt}
          </Text>
        </Box>
        <Box style={{ alignSelf: 'end' }}>
          <Text small color="textSubtle">
            {isCancelled ? `X Cancelled` : isExpired ? 'Expired' : null}
          </Text>
          <Text small color="success">
            {isExecuted ? `✓ Successful` : null}
          </Text>
        </Box>
      </Flex>
    </Flex>
  )
}

const OpenOrderDataRow = ({ order }) => {
  const {
    inputToken,
    outputToken,
    inputAmount,
    outputAmount,
    executionPrice,
    invertedExecutionPrice,
    isOpen,
    isCancelled,
    isExecuted,
    isSubmissionPending,
    isCancellationPending,
    realExecutionPriceAsString,
  } = useFormattedOrderData(order)
  const { theme } = useTheme()
  const { handleLimitOrderCancellation } = useGelatoLimitOrdersHandlers()
  const { isMobile } = useMatchBreakpoints()

  const [{ cancellationErrorMessage, attemptingTxn, txHash }, setCancellationState] = useState<{
    attemptingTxn: boolean
    cancellationErrorMessage: string | undefined
    txHash: string | undefined
  }>({
    attemptingTxn: false,
    cancellationErrorMessage: undefined,
    txHash: undefined,
  })

  const onCancelOrder = useCallback(() => {
    if (!handleLimitOrderCancellation) {
      return
    }
    setCancellationState({
      attemptingTxn: true,
      cancellationErrorMessage: undefined,
      txHash: undefined,
    })

    const orderDetails =
      inputToken?.symbol && outputToken?.symbol && inputAmount && outputAmount
        ? {
            inputTokenSymbol: inputToken.symbol,
            outputTokenSymbol: outputToken.symbol,
            inputAmount,
            outputAmount,
          }
        : undefined
    handleLimitOrderCancellation(order, orderDetails)
      .then(({ hash }) => {
        setCancellationState({
          attemptingTxn: false,
          cancellationErrorMessage: undefined,
          txHash: hash,
        })
      })
      .catch((error) => {
        setCancellationState({
          attemptingTxn: false,
          cancellationErrorMessage: error.message,
          txHash: undefined,
        })
      })
  }, [handleLimitOrderCancellation, inputAmount, outputAmount, inputToken?.symbol, outputToken?.symbol, order])

  return (
    <Flex flexDirection={!isMobile ? 'row' : 'column'} justifyContent="space-around" mx="20px">
      <Flex alignItems="top" width="80%">
        <TokenImage token={outputToken as Token} width={35} height={35} />
        <Flex flexDirection="column" height="100%" width="100%" ml="10px">
          <Flex alignItems="center" mb="5px">
            <Box style={{ marginRight: '5px' }}>
              <Text color="text">
                {inputAmount} {inputToken?.symbol}
              </Text>
            </Box>
            <ArrowForwardIcon fill={theme.colors.primary} style={{ marginRight: '5px' }} />
            <Box>
              <Text color="text">
                {outputAmount} {outputToken?.symbol}
              </Text>
            </Box>
          </Flex>
          {!isMobile && (
            <Flex alignItems="center">
              <Box>
                <Text color="textSubtle">
                  {realExecutionPriceAsString === 'never executes'
                    ? 'Large price change, unlikely to execute'
                    : `1 ${inputToken?.symbol} = ${realExecutionPriceAsString} ${outputToken?.symbol}`}
                </Text>
              </Box>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Flex justifyContent="space-between">
        {isMobile && (
          <Flex alignItems="center">
            <Box>
              <Text color="textSubtle">
                {realExecutionPriceAsString === 'never executes'
                  ? 'Large price change, unlikely to execute'
                  : `1 ${inputToken?.symbol} = ${realExecutionPriceAsString} ${outputToken?.symbol}`}
              </Text>
            </Box>
          </Flex>
        )}
        {isOpen && !isSubmissionPending && (
          <Button mt={!isMobile ? '0px' : '10px'} width="100px" onClick={onCancelOrder}>
            Cancel
          </Button>
        )}
      </Flex>
    </Flex>
  )
}

const TableLoader: React.FC = () => {
  const loadingRow = (
    <Box style={{ margin: '0 10px' }}>
      <Skeleton />
    </Box>
  )
  return (
    <>
      {loadingRow}
      {loadingRow}
    </>
  )
}

export const ResponsiveGrid = styled.div`
  display: grid;
  justify-content: start;
  align-items: center;

  padding: 12px 24px;

  grid-template-columns: 2fr repeat(2, 3fr) 2fr;

  @media screen and (max-width: 900px) {
    grid-template-columns: 1fr 4fr 2fr 1fr;
  }

  @media screen and (max-width: 800px) {
    grid-template-columns: 1fr 4fr 2fr 1fr;
  }

  @media screen and (max-width: 670px) {
    grid-template-columns: 1fr 2fr 2fr 1fr;
  }
`

const LimitOrdersTable = () => {
  const { theme } = useTheme()
  const [page, setPage] = useState(1)
  const [maxPage, setMaxPage] = useState(1)
  const { t } = useTranslation()
  const { isDesktop } = useMatchBreakpoints()
  const [isOpenOrders, setIsOpenOrders] = useState(true)

  const { open, cancelled, executed, expired } = useGelatoLimitOrdersHistory()
  const openOrders = useMemo(() => {
    return [...open.confirmed, ...open.pending].sort((a: any, b: any) => Number(b.updatedAt) - Number(a.updatedAt))
  }, [open.confirmed, open.pending])
  const pastOrders = useMemo(() => {
    return [...executed, ...cancelled.confirmed, ...cancelled.pending, ...expired].sort(
      (a: any, b: any) => Number(b.updatedAt) - Number(a.updatedAt),
    )
  }, [cancelled.confirmed, cancelled.pending, executed, expired])

  const paginatedOpenTransactions = useMemo(() => {
    return openOrders
      ? [...openOrders].slice(ITEMS_PER_INFO_TABLE_PAGE * (page - 1), page * ITEMS_PER_INFO_TABLE_PAGE)
      : []
  }, [openOrders, page])
  const paginatedPastTransactions = useMemo(() => {
    return pastOrders
      ? [...pastOrders].slice(ITEMS_PER_INFO_TABLE_PAGE * (page - 1), page * ITEMS_PER_INFO_TABLE_PAGE)
      : []
  }, [pastOrders, page])

  // Update maxPage based on amount of items & applied filtering
  useEffect(() => {
    if (isOpenOrders && openOrders) {
      if (openOrders.length % ITEMS_PER_INFO_TABLE_PAGE === 0) {
        setMaxPage(Math.floor(openOrders.length / ITEMS_PER_INFO_TABLE_PAGE))
      } else {
        setMaxPage(Math.floor(openOrders.length / ITEMS_PER_INFO_TABLE_PAGE) + 1)
      }
    } else if (!isOpenOrders && pastOrders) {
      if (pastOrders.length % ITEMS_PER_INFO_TABLE_PAGE === 0) {
        setMaxPage(Math.floor(pastOrders.length / ITEMS_PER_INFO_TABLE_PAGE))
      } else {
        setMaxPage(Math.floor(pastOrders.length / ITEMS_PER_INFO_TABLE_PAGE) + 1)
      }
    }
  }, [openOrders, pastOrders, isOpenOrders])

  return (
    <TableWrapper background={theme.card.background} border={theme.radii.card} mt="20px">
      <Flex alignSelf="center" width="100%" ml="30px">
        <Box
          onClick={() => {
            if (isOpenOrders) return
            setIsOpenOrders(!isOpenOrders)
            setPage(1)
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '30px',
            marginRight: '10px',
            height: '40px',
            width: isDesktop ? '25%' : '35%',
            backgroundColor: isOpenOrders ? theme.colors.primary : theme.colors.inputBackground,
            cursor: 'pointer',
          }}
        >
          <Flex justifyContent="center" alignItems="center">
            <Text bold color={isOpenOrders ? 'white' : theme.colors.primary}>
              Open Orders
            </Text>
          </Flex>
        </Box>
        <Box
          onClick={() => {
            if (!isOpenOrders) return
            setIsOpenOrders(!isOpenOrders)
            setPage(1)
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '30px',
            marginRight: '10px',
            height: '40px',
            width: isDesktop ? '20%' : '25%',
            backgroundColor: isOpenOrders ? theme.colors.inputBackground : theme.colors.primary,
            cursor: 'pointer',
          }}
        >
          <Flex justifyContent="center" alignItems="center">
            <Text bold color={isOpenOrders ? theme.colors.primary : 'white'}>
              History
            </Text>
          </Flex>
        </Box>
      </Flex>
      <Break />
      {isOpenOrders ? (
        paginatedOpenTransactions ? (
          paginatedOpenTransactions.length > 0 ? (
            <>
              {paginatedOpenTransactions.map((order, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={index}>
                  <OpenOrderDataRow order={order} />
                  <Break />
                </React.Fragment>
              ))}
              <PageButtons>
                <Arrow
                  onClick={() => {
                    setPage(page === 1 ? page : page - 1)
                  }}
                >
                  <ArrowBackIcon color={page === 1 ? 'textSubtle' : 'primary'} />
                </Arrow>

                <Text>{t('Page %page% of %maxPage%', { page, maxPage })}</Text>
                <Arrow
                  onClick={() => {
                    setPage(page === maxPage ? page : page + 1)
                  }}
                >
                  <ArrowForwardIcon color={page === maxPage ? 'textSubtle' : 'primary'} />
                </Arrow>
              </PageButtons>
            </>
          ) : (
            <Flex justifyContent="center" mb="20px">
              <Text>{t('No Orders')}</Text>
            </Flex>
          )
        ) : (
          <TableLoader />
        )
      ) : !isOpenOrders ? (
        paginatedPastTransactions ? (
          paginatedPastTransactions.length > 0 ? (
            <>
              {paginatedPastTransactions.map((order, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={index}>
                  <PastOrderDataRow order={order} />
                  <Break />
                </React.Fragment>
              ))}
              <PageButtons>
                <Arrow
                  onClick={() => {
                    setPage(page === 1 ? page : page - 1)
                  }}
                >
                  <ArrowBackIcon color={page === 1 ? 'textSubtle' : 'primary'} />
                </Arrow>

                <Text>{t('Page %page% of %maxPage%', { page, maxPage })}</Text>
                <Arrow
                  onClick={() => {
                    setPage(page === maxPage ? page : page + 1)
                  }}
                >
                  <ArrowForwardIcon color={page === maxPage ? 'textSubtle' : 'primary'} />
                </Arrow>
              </PageButtons>
            </>
          ) : (
            <Flex justifyContent="center" mb="20px">
              <Text>{t('No Orders')}</Text>
            </Flex>
          )
        ) : (
          <TableLoader />
        )
      ) : null}
    </TableWrapper>
  )
}

export default LimitOrdersTable
